import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PasswordInput from '../components/PasswordInput';
import { API_BASE } from '../lib/api';

// APPROVE.1a (#3183) / #3190 — /onboard/set-password?token=<raw>
//
// The link a partner-managed client's contact receives when their workspace
// is created (or re-sent by the partner). Sets their password and verifies
// their address via POST /auth/managed-onboard/complete, then sends them to
// /login — whose routing takes a CLIENT-role user to the Client Portal.
//
// Same token hygiene as ResetPasswordPage: the token is snapshotted into
// state and stripped from the URL on mount; raw fetch (not apiFetch) so the
// 410 / 400 statuses drive distinct states; success requires `ok: true` in
// the body so an SPA-fallback 200 can't masquerade as success.

const MIN_PASSWORD = 8;
const MAX_PASSWORD = 72;

const inputStyle: React.CSSProperties = {
  display: 'block', width: '100%', height: 44, background: 'var(--surface-alt)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '0 14px',
  fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', outline: 'none', boxSizing: 'border-box',
};
const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '0 0 8px',
};
const bodyStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.55, margin: '0 0 20px',
};

type PageState =
  | { kind: 'no-token' }
  | { kind: 'form'; error?: string }
  | { kind: 'submitting' }
  | { kind: 'expired-or-invalid' }
  | { kind: 'success' };

export default function ManagedOnboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token] = useState(() => searchParams.get('token') ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [state, setState] = useState<PageState>(token ? { kind: 'form' } : { kind: 'no-token' });

  useEffect(() => {
    if (token) window.history.replaceState({}, '', '/onboard/set-password');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.kind === 'success') {
      const t = setTimeout(() => navigate('/login?onboarded=1'), 1500);
      return () => clearTimeout(t);
    }
  }, [state.kind, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (state.kind !== 'form') return;
    if (password.length < MIN_PASSWORD || password.length > MAX_PASSWORD) {
      setState({ kind: 'form', error: `Password must be ${MIN_PASSWORD}-${MAX_PASSWORD} characters.` });
      return;
    }
    if (password !== confirmPassword) {
      setState({ kind: 'form', error: 'Passwords do not match.' });
      return;
    }
    setState({ kind: 'submitting' });
    try {
      const res = await fetch(`${API_BASE}/auth/managed-onboard/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (res.status === 200) {
        const body = await res.json().catch(() => null) as { ok?: boolean } | null;
        setState(body?.ok === true ? { kind: 'success' } : { kind: 'form', error: 'Something went wrong. Please try again.' });
        return;
      }
      if (res.status === 410) { setState({ kind: 'expired-or-invalid' }); return; }
      if (res.status === 400) {
        const body = await res.json().catch(() => ({}));
        setState({ kind: 'form', error: typeof body?.error === 'string' ? body.error : 'That password isn’t acceptable. Try a different one.' });
        return;
      }
      setState({ kind: 'form', error: 'Something went wrong. Please try again.' });
    } catch {
      setState({ kind: 'form', error: 'Network error. Please check your connection and try again.' });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div style={{ width: '100%', maxWidth: 400, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-lg)', padding: 40 }}>
        {(state.kind === 'no-token' || state.kind === 'expired-or-invalid') && (
          <>
            <h1 style={headingStyle}>{state.kind === 'no-token' ? 'Invalid link' : 'This link has expired'}</h1>
            <p data-testid={state.kind === 'no-token' ? 'onboard-no-token' : 'onboard-expired'} style={bodyStyle}>
              {state.kind === 'no-token'
                ? 'This page can only be reached from your workspace invitation email.'
                : 'Your workspace link has expired or has already been used.'}
              {' '}Ask your delivery partner to send you a new one. If you already set a password, sign in instead.
            </p>
          </>
        )}

        {state.kind === 'success' && (
          <>
            <h1 style={headingStyle}>You&rsquo;re all set</h1>
            <p data-testid="onboard-success" style={bodyStyle}>Your password is set. Taking you to sign in&hellip;</p>
          </>
        )}

        {(state.kind === 'form' || state.kind === 'submitting') && (
          <>
            <h1 style={headingStyle}>Set your password</h1>
            <p style={{ ...bodyStyle, fontSize: 13, margin: '0 0 24px' }}>
              Your delivery partner has set up your BluePrint workspace. Choose a password to open it. {MIN_PASSWORD}&ndash;{MAX_PASSWORD} characters.
            </p>
            <form onSubmit={handleSubmit}>
              {state.kind === 'form' && state.error && (
                <div role="alert" data-testid="onboard-error" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginBottom: 16 }}>
                  {state.error}
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="onboard-password" style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>Password</label>
                <PasswordInput id="onboard-password" required autoComplete="new-password" minLength={MIN_PASSWORD} maxLength={MAX_PASSWORD}
                  value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label htmlFor="onboard-confirm" style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>Confirm password</label>
                <PasswordInput id="onboard-confirm" required autoComplete="new-password" minLength={MIN_PASSWORD} maxLength={MAX_PASSWORD}
                  value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={inputStyle} />
              </div>
              <button type="submit" disabled={state.kind === 'submitting'} style={{
                display: 'block', width: '100%', height: 44, background: 'var(--primary)', color: 'white',
                fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: 'none',
                cursor: state.kind === 'submitting' ? 'not-allowed' : 'pointer', opacity: state.kind === 'submitting' ? 0.7 : 1,
              }}>
                {state.kind === 'submitting' ? 'Setting up…' : 'Set password and continue'}
              </button>
            </form>
          </>
        )}

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 24 }}>
          <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}>Go to sign in</Link>
        </p>
      </div>
    </div>
  );
}
